
import api from '@/api'
import config from '@/config'
import AuthService from '@/services/support/AuthService'
import userInfoStore from '@/store/userInfo'
import { UserInfo } from '@/types/Auth'
import { GENERAL_REGEXPS } from '@/types/RegExpressions'
import Vue from 'vue'
import Component from 'vue-class-component'

interface UserProfileData {
  firstName: string
  lastName: string
  phoneExtra: string
  emailExtra: string
}

@Component({
  name: 'UserProfile'
})
class UserProfile extends Vue {
  form: UserInfo = new UserInfo()
  readOnly: boolean = true
  backupProfile: UserProfileData = {
    firstName: '',
    lastName: '',
    emailExtra: '',
    phoneExtra: ''
  }
  profile: UserProfileData = {
    firstName: '',
    lastName: '',
    emailExtra: '',
    phoneExtra: ''
  }

  created () {
    this.loadProfile()
  }

  /**
   * Sincronizes the backup and the profil user with the current user info
   *
   * @param userInfo with current values
   */
  sincronizedUserInfo (userInfo: UserInfo) {
    this.form = userInfo
    this.setProfile(this.backupProfile, this.form)
    this.setProfile(this.profile, this.form)
  }

  /**
   * Gets the user info and sincronized its values
   * with two objects: the backup and the profile object
   */
  async loadProfile () {
    await AuthService.getUserInfo()
      .then(() => {
        this.sincronizedUserInfo(userInfoStore.userInfo)
      })
  }

  /**
   * Assigns the profile values to a specific target
   *
   * @param target to set the values
   * @param profile from get values to set
   */
  setProfile (target: UserProfileData, profile: UserProfileData) {
    target.firstName = profile.firstName || ''
    target.lastName = profile.lastName || ''
    target.emailExtra = profile.emailExtra || ''
    target.phoneExtra = profile.phoneExtra || ''
  }

  /**
   * Validates if has change some value in the form
   */
  profileHasChanged () {
    return JSON.stringify(this.profile) !== JSON.stringify(this.backupProfile)
  }

  /**
   * Make the request to save the values
   *
   * @param profile with the current values
   */
  async saveProfile (profile: UserProfileData) {
    const url = `${config.apiUsersUrl}me`
    const data = {
      firstName: profile.firstName || null,
      lastName: profile.lastName || null,
      emailExtra: profile.emailExtra || null,
      phoneExtra: profile.phoneExtra || null
    }
    const request = await api.put(url, data)
    return request.data
  }

  /**
   * Validates that exist some change in the form
   * and make the request to update the values
   */
  async updateProfile () {
    // Only when sme value has changed
    if (this.profileHasChanged()) {
      this.saveProfile(this.profile)
        // display a feedback and reload the user info
        .then((response) => {
          this.$bvToast.toast(
            'El perfil ha sido actualizado correctamente.',
            {
              title: 'Cambios guardados',
              solid: true,
              variant: 'success',
              autoHideDelay: 4000,
              appendToast: true
            }
          )
          this.sincronizedUserInfo(response)
          this.readOnly = true
        })
        // manage the error
        .catch(() => {
          this.$bvToast.toast(
            `No ha sido posible guardar los cambios.
             Revisa los campos marcados con error.`,
            {
              title: 'Cambios no guardados',
              solid: true,
              variant: 'danger',
              autoHideDelay: 4000,
              appendToast: true
            }
          )
        })
    } else {
      this.readOnly = true
    }
  }

  /**
   * On cancel the update profile
   * If the profile has some change reload the backup values
   */
  cancelUpdateProfile () {
    if (this.profileHasChanged()) {
      this.profile = { ...this.backupProfile }
    }
    this.readOnly = true
  }

  /** Validates the email extra format */
  get validateEmail () {
    let email = this.profile.emailExtra
    return email
      ? new RegExp(GENERAL_REGEXPS.email).test(email)
      : null
  }

  /** Validates the phone extra format */
  get validatePhone () {
    let phone = this.profile.phoneExtra
    return phone
      ? new RegExp(GENERAL_REGEXPS.phone).test(phone)
      : null
  }

  /** Validates the email and phone satisfy the formats */
  get validateFormatValues () {
    return (this.profile.emailExtra ? this.validateEmail : true) &&
      (this.profile.phoneExtra ? this.validatePhone : true)
  }
}
export default UserProfile
